import { useEditor, EditorContent, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  Bold,
  Strikethrough,
  Italic,
  List,
  ListOrdered,
  Link as LinkIcon,
  Heading,
  Maximize2,
  Minimize2,
  Image as ImageIcon,
} from "lucide-react";
import { Toggle } from "@/app/common/common-components/ui/Toggle";
import { Separator } from "@/app/common/common-components/ui/Separator";
import { useEffect, useState, useImperativeHandle, forwardRef } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/app/common/common-components/ui/Popover";
import { Input } from "@/app/common/common-components/ui/Input";
import { Button } from "@/app/common/common-components/ui/Button";
import { Link } from "@tiptap/extension-link";
import ImageResize from "tiptap-extension-resize-image";
import { cn } from "@/app/common/common-functions/functions";

export interface SimpleTextEditorRef {
  insertTextAtCursor: (text: string) => void;
  focus: () => void;
  getEditor: () => Editor | null;
}

const SimpleTextEditor = forwardRef<
  SimpleTextEditorRef,
  {
    format?: "markdown" | "html";
    value: string;
    onChange: (value: string) => void;
    disabled?: boolean;
    className?: string;
  }
>(({ format = "html", value, onChange, disabled, className }, ref) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const editor = useEditor({
    editorProps: {
      attributes: {
        class:
          "tw-w-full tw-rounded-md tw-rounded-br-none tw-rounded-bl-none tw-border tw-border-input tw-bg-transparent tw-px-3 tw-py-2 tw-border-b-0 tw-text-sm tw-ring-offset-background tw-placeholder:text-muted-foreground focus-visible:tw-outline-none disabled:tw-cursor-not-allowed disabled:tw-opacity-50 tw-overflow-auto tw-h-full",
      },
    },
    extensions: [
      Link.configure({
        autolink: false,
        openOnClick: false,
        linkOnPaste: true,
      }),
      ImageResize.configure({
        HTMLAttributes: {
          class: "tw-max-w-full tw-h-auto tw-rounded-md tw-cursor-pointer",
        },
        inline: true,
        allowBase64: true,
      }),
      StarterKit.configure({
        heading: {
          HTMLAttributes: {
            class: "tw-font-semibold tw-text-3xl",
          },
          levels: [3],
        },
        orderedList: {
          HTMLAttributes: {
            class: "tw-list-decimal tw-pl-4",
          },
        },
        bulletList: {
          HTMLAttributes: {
            class: "tw-list-disc tw-pl-4",
          },
        },
      }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  useImperativeHandle(
    ref,
    () => ({
      insertTextAtCursor: (text: string) => {
        if (editor) {
          editor.chain().focus().insertContent(text).run();
        }
      },
      focus: () => {
        editor?.chain().focus().run();
      },
      getEditor: () => editor,
    }),
    [editor]
  );

  useEffect(() => {
    const editorContent = editor?.getHTML();

    if (JSON.stringify(editorContent) === JSON.stringify(value)) return; // Prevent changing the editor content when the value is the same
    editor?.commands.setContent(value);
  }, [value, editor]);

  return (
    <div>
      <div
        className={cn(
          `${isExpanded ? "tw-h-[400px]" : "tw-h-[150px]"} ${className}`
        )}
      >
        <EditorContent
          editor={editor}
          disabled={disabled}
          className="tw-h-full"
        />
      </div>
      {editor ? (
        <RichTextEditorToolbar
          editor={editor}
          isExpanded={isExpanded}
          onToggleExpand={setIsExpanded}
        />
      ) : null}
    </div>
  );
});

SimpleTextEditor.displayName = "SimpleTextEditor";

const RichTextEditorToolbar = ({
  editor,
  isExpanded,
  onToggleExpand,
}: {
  editor: Editor;
  isExpanded: boolean;
  onToggleExpand: (expanded: boolean) => void;
}) => {
  const [linkPopoverOpen, setLinkPopoverOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [imagePopoverOpen, setImagePopoverOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState("");

  const ensureUrlHasProtocol = (rawUrl: string): string => {
    const url = (rawUrl || "").trim();
    if (url === "") return url;
    // If URL already has a scheme like http:, https:, mailto:, tel:, ftp:, etc.
    if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(url)) return url;
    // If URL starts with protocol-relative form //example.com, prefix https:
    if (url.startsWith("//")) return `https:${url}`;
    // Default to https:// when no protocol present
    return `https://${url}`;
  };

  const handleOpenLinkPopover = () => {
    const currentLink = editor.getAttributes("link").href || "";
    setLinkUrl(currentLink);
    setLinkPopoverOpen(true);
  };

  const handleSetLink = () => {
    if (linkUrl) {
      const normalizedUrl = ensureUrlHasProtocol(linkUrl);
      editor
        .chain()
        .focus()
        .extendMarkRange("link")
        .setLink({ href: normalizedUrl })
        .run();
    } else {
      editor.chain().focus().unsetLink().run();
    }
    setLinkPopoverOpen(false);
  };

  const handleOpenImagePopover = () => {
    setImageUrl("");
    setImagePopoverOpen(true);
  };

  const handleSetImage = () => {
    if (imageUrl) {
      editor.chain().focus().setImage({ src: imageUrl }).run();
    }
    setImagePopoverOpen(false);
    setImageUrl("");
  };

  const handleImageFileUpload = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target?.result as string;
        editor.chain().focus().setImage({ src: base64 }).run();
        setImagePopoverOpen(false);
        setImageUrl("");
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="tw-flex tw-flex-row tw-items-center tw-gap-1 tw-rounded-bl-md tw-rounded-br-md tw-border tw-border-input tw-bg-transparent tw-p-1">
      <Toggle
        size="sm"
        pressed={editor.isActive("bold")}
        onPressedChange={() => editor.chain().focus().toggleBold().run()}
      >
        <Bold className="tw-h-4 tw-w-4" />
      </Toggle>
      <Toggle
        size="sm"
        pressed={editor.isActive("italic")}
        onPressedChange={() => editor.chain().focus().toggleItalic().run()}
      >
        <Italic className="tw-h-4 tw-w-4" />
      </Toggle>
      <Toggle
        size="sm"
        pressed={editor.isActive("strike")}
        onPressedChange={() => editor.chain().focus().toggleStrike().run()}
      >
        <Strikethrough className="tw-h-4 tw-w-4" />
      </Toggle>
      <Separator orientation="vertical" className="tw-h-8 tw-w-[1px]" />
      <Toggle
        size="sm"
        pressed={editor.isActive("bulletList")}
        onPressedChange={() => editor.chain().focus().toggleBulletList().run()}
      >
        <List className="tw-h-4 tw-w-4" />
      </Toggle>
      <Toggle
        size="sm"
        pressed={editor.isActive("orderedList")}
        onPressedChange={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <ListOrdered className="tw-h-4 tw-w-4" />
      </Toggle>
      <Separator orientation="vertical" className="tw-h-8 tw-w-[1px]" />
      <Popover
        open={linkPopoverOpen}
        onOpenChange={setLinkPopoverOpen}
        modal={true}
      >
        <PopoverTrigger asChild>
          <button
            className={`tw-p-2 ${editor.isActive("link") ? "tw-text-blue-500" : ""}`}
            onClick={handleOpenLinkPopover}
            title="Add or edit link"
          >
            <LinkIcon className="tw-h-4 tw-w-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="tw-w-64">
          <div className="tw-flex tw-flex-col tw-gap-2">
            <Input
              placeholder="Enter URL"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              className="tw-w-full"
            />
            <div className="tw-flex tw-justify-end tw-gap-2">
              <Button
                variant="outline"
                onClick={() => setLinkPopoverOpen(false)}
              >
                Cancel
              </Button>
              <Button onClick={handleSetLink}>Save</Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
      <Popover
        open={imagePopoverOpen}
        onOpenChange={setImagePopoverOpen}
        modal={true}
      >
        <PopoverTrigger asChild>
          <button
            className="tw-p-2"
            onClick={handleOpenImagePopover}
            title="Add image"
          >
            <ImageIcon className="tw-h-4 tw-w-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="tw-w-80">
          <div className="tw-flex tw-flex-col tw-gap-3">
            <div className="tw-text-sm tw-font-medium">Add Image</div>

            <div className="tw-flex tw-items-center tw-gap-2">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageFileUpload}
                className="tw-file:tw-mr-4 tw-file:tw-py-1 tw-file:tw-px-2 tw-file:tw-rounded-md tw-file:tw-border-0 tw-file:tw-text-xs tw-file:tw-font-medium tw-file:tw-bg-primary tw-file:tw-text-primary-foreground hover:tw-file:tw-bg-primary/90 tw-text-sm"
              />
            </div>
            <div className="tw-flex tw-justify-end tw-gap-2">
              <Button
                variant="outline"
                onClick={() => setImagePopoverOpen(false)}
              >
                Cancel
              </Button>
              <Button onClick={handleSetImage}>Add Image</Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
      <Separator orientation="vertical" className="tw-h-8 tw-w-[1px]" />
      <Toggle
        size="sm"
        pressed={editor.isActive("heading", { level: 3 })}
        onPressedChange={() =>
          editor.chain().focus().toggleHeading({ level: 3 }).run()
        }
        className="tw-px-3 tw-py-2 tw-font-semibold"
      >
        <Heading className="tw-h-4 tw-w-4" />
      </Toggle>
      <Separator orientation="vertical" className="tw-h-8 tw-w-[1px]" />
      <Toggle size="sm" pressed={isExpanded} onPressedChange={onToggleExpand}>
        {isExpanded ? (
          <Minimize2 className="tw-h-4 tw-w-4" />
        ) : (
          <Maximize2 className="tw-h-4 tw-w-4" />
        )}
      </Toggle>
    </div>
  );
};

export default SimpleTextEditor;
